/* ============================================================
   SEARCH — de zoek-overlay openen, sluiten en vullen.

   Het origineel draait op Algolia. Hier gebruiken we Shopify's eigen
   predictive search (/search/suggest.json). Dat scheelt een dienst, een
   sleutel en een extra bundel, en de DOM blijft gelijk.

   Zonder JavaScript werkt het nog steeds: het formulier post naar /search
   en de dialog sluit via <form method="dialog">.
   ============================================================ */

const RECENT_KEY = 'search:recent';
const RECENT_MAX = 5;
const DEBOUNCE_MS = 250;

const rustig = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function leesRecent() {
  try {
    const ruw = localStorage.getItem(RECENT_KEY);
    return ruw ? JSON.parse(ruw).filter((t) => typeof t === 'string').slice(0, RECENT_MAX) : [];
  } catch (e) {
    return [];                        // privémodus of vol geheugen: gewoon zonder
  }
}

function bewaarRecent(term) {
  const schoon = (term || '').trim();
  if (!schoon) return;
  try {
    const lijst = [schoon, ...leesRecent().filter((t) => t !== schoon)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(lijst));
  } catch (e) { /* niet erg */ }
}

class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-search-dialog]');
    this.input = this.querySelector('.header-search__input');
    this.form = this.querySelector('.header-search__form-search');
    this.recentBox = this.querySelector('[data-recent-searches]');
    this.paneel = this.querySelector('[data-panel]');
    this.legeStaat = this.querySelector('[data-empty-state]');
    if (!this.dialog || !this.input) return;

    this.labels = {
      producten: this.dataset.labelProducts || 'Products',
      allesZien: this.dataset.labelSeeAll || 'See all',
      geenResultaat: this.dataset.labelNoResults || 'No results',
      recent: this.dataset.labelRecent || 'Recent searches',
    };

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.open = this.open.bind(this);

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('search:open', this.open);
    this.input.addEventListener('input', this.onInput);
    this.form.addEventListener('submit', () => bewaarRecent(this.input.value));
    this.dialog.addEventListener('close', () => this.naSluiten());
    // klik op de achtergrond van de dialog sluit hem
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('search:open', this.open);
  }

  onDocumentClick(e) {
    const knop = e.target.closest('[data-btn-toogle-search]');
    if (!knop) return;
    // de sluitknop zit in <form method="dialog"> en doet zijn werk zelf
    if (this.dialog.contains(knop)) return;
    e.preventDefault();
    this.open();
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    document.body.classList.add('is-overflow-hidden');
    this.toonRecent();
    // focus pas na het openen, anders springt de pagina
    window.requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
  }

  naSluiten() {
    document.body.classList.remove('is-overflow-hidden');
    this.input.value = '';
    this.toonPaneel(false);
    this.paneel.innerHTML = '';
  }

  /* ---------- recente zoekopdrachten ---------- */

  toonRecent() {
    const lijst = leesRecent();
    if (!lijst.length) { this.recentBox.hidden = true; this.recentBox.innerHTML = ''; return; }
    const icoon = this.querySelector('[data-recent-search-icon]');
    this.recentBox.innerHTML =
      `<span class="label-m-alternate">${this.escape(this.labels.recent)}</span>` +
      `<ul class="header-search__recent-list">` +
      lijst.map((t) =>
        `<li><a href="${this.zoekUrl(t)}">` +
        `<span class="header-search__recent-icon">${icoon ? icoon.innerHTML : ''}</span>` +
        `<span>${this.escape(t)}</span></a></li>`).join('') +
      `</ul>`;
    this.recentBox.hidden = false;
  }

  /* ---------- predictive search ---------- */

  onInput() {
    clearTimeout(this.timer);
    const term = this.input.value.trim();
    if (term.length < 2) { this.toonPaneel(false); return; }
    this.timer = setTimeout(() => this.zoek(term), DEBOUNCE_MS);
  }

  async zoek(term) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const url = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}`
      .replace(/\/$/, '') +
      `/search/suggest.json?q=${encodeURIComponent(term)}` +
      `&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`;
    try {
      const res = await fetch(url, { signal: this.controller.signal });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      this.toonResultaten(term, (data.resources && data.resources.results && data.resources.results.products) || []);
    } catch (e) {
      if (e.name !== 'AbortError') this.toonResultaten(term, []);
    }
  }

  toonResultaten(term, producten) {
    if (!producten.length) {
      this.paneel.innerHTML = `<p class="body-m-light">${this.escape(this.labels.geenResultaat)}</p>`;
      this.toonPaneel(true);
      return;
    }
    this.paneel.innerHTML =
      `<div class="header-search__product-result">` +
        `<div class="result-title">` +
          `<span class="label-m-alternate">${this.escape(this.labels.producten)}</span>` +
          `<a class="button--header-search label-s-regular" href="${this.zoekUrl(term)}">` +
            `${this.escape(this.labels.allesZien)}</a>` +
        `</div>` +
        `<div class="container-result">` +
          producten.map((p) => this.productKaart(p)).join('') +
        `</div>` +
      `</div>`;
    this.toonPaneel(true);
  }

  productKaart(p) {
    const beeld = p.featured_image && p.featured_image.url
      ? `<img src="${p.featured_image.url}" alt="${this.escape(p.featured_image.alt || p.title)}" loading="lazy">`
      : '';
    return `<div class="product-card">` +
      `<a class="product-card__link" href="${p.url}">` +
        `<div class="product-card__image">${beeld}</div>` +
      `</a>` +
      `<div class="product-card__container">` +
        `<div class="product-card__content">` +
          `<a href="${p.url}"><span class="product-card__title label-m-regular">${this.escape(p.title)}</span></a>` +
          `<span class="product-card__informations label-s-light">${p.price ? this.escape(p.price) : ''}</span>` +
        `</div>` +
      `</div>` +
    `</div>`;
  }

  /* ---------- hulpjes ---------- */

  toonPaneel(aan) {
    this.legeStaat.hidden = aan;
    this.paneel.hidden = !aan;
    if (aan && !rustig()) this.paneel.scrollTop = 0;
  }

  zoekUrl(term) {
    return `/search?q=${encodeURIComponent(term)}`;
  }

  escape(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

if (!customElements.get('c-header-search')) {
  customElements.define('c-header-search', HeaderSearch);
}
